/**
 * Canonical Hightouch responsive style block.
 *
 * Components are stored as headless fragments that carry the canonical `ht-*`
 * classes inline but NOT the *definitions* of those classes. The definitions
 * live in a single `@media` block that must be injected wherever a component
 * (or a template that hosts components) is rendered:
 *
 *   - component library preview (wrap the fragment for display)
 *   - templates (inject into the head `<style>` on import / lazily) so a
 *     dropped-in component just works
 *
 * The block is bounded by `ht:responsive-block` comment markers so it can be
 * deterministically located, replaced, or stripped without reparsing the HTML
 * (important: reparsing email HTML can mangle `<!--[if mso]>` conditionals).
 */

export const HT_RESPONSIVE_BLOCK_START_MARKER = "ht:responsive-block start";
export const HT_RESPONSIVE_BLOCK_END_MARKER = "ht:responsive-block end";

export const DEFAULT_MOBILE_BREAKPOINT_PX = 600;
export const DEFAULT_CONTENT_WIDTH_PX = 600;

// Last-resort guardrails for a raw jsonb value. In practice callers pass the
// already-resolved config (`resolveEmailStyleConfiguration` clamps to the
// tighter UI range), so these only fire if raw jsonb reaches the render path
// directly; the wide bounds just reject absurd values without second-guessing
// the authoritative resolver.
const MIN_BREAKPOINT_PX = 1;
const MAX_BREAKPOINT_PX = 2000;

const MIN_CONTENT_WIDTH_PX = 1;
const MAX_CONTENT_WIDTH_PX = 2000;

export interface EmailStyleConfiguration {
  /** Mobile breakpoint in px (e.g. 600, 640, or a user-defined value). */
  mobileBreakpointWidth?: number | null;
  /**
   * Desktop content column width in px the standalone component wrapper caps at.
   * Independent of `mobileBreakpointWidth` — the breakpoint says *when* the
   * mobile layout applies, this says *how wide* the desktop column is.
   */
  contentWidthPx?: number | null;
}

export function resolveMobileBreakpointPx(value: unknown): number {
  const n = typeof value === "number" ? value : Number(value);
  if (!Number.isFinite(n)) return DEFAULT_MOBILE_BREAKPOINT_PX;
  const int = Math.round(n);
  if (int < MIN_BREAKPOINT_PX || int > MAX_BREAKPOINT_PX) {
    return DEFAULT_MOBILE_BREAKPOINT_PX;
  }
  return int;
}

export function resolveContentWidthPx(value: unknown): number {
  const n = typeof value === "number" ? value : Number(value);
  if (!Number.isFinite(n)) return DEFAULT_CONTENT_WIDTH_PX;
  const int = Math.round(n);
  if (int < MIN_CONTENT_WIDTH_PX || int > MAX_CONTENT_WIDTH_PX) {
    return DEFAULT_CONTENT_WIDTH_PX;
  }
  return int;
}

/**
 * The comment-bounded canonical block WITHOUT a surrounding `<style>` tag.
 * Use this when injecting into an existing `<style>` element.
 */
export function buildCanonicalInnerBlock(
  config?: EmailStyleConfiguration,
): string {
  const breakpoint = resolveMobileBreakpointPx(config?.mobileBreakpointWidth);

  return `/* ${HT_RESPONSIVE_BLOCK_START_MARKER} */
@media only screen and (max-width: ${breakpoint}px) {
  /* Show/hide swaps */
  .ht-mobile-hide  { display: none !important; }
  .ht-desktop-hide { display: block !important; max-height: none !important; overflow: visible !important; }
  tr.ht-desktop-hide { display: table-row !important; }
  td.ht-desktop-hide { display: table-cell !important; }

  /* Dual-frame toggle (whole desktop/mobile subtrees) */
  .ht-desktop { display: none !important; max-height: 0 !important; overflow: hidden !important; }
  .ht-mobile  { display: block !important; max-height: none !important; overflow: visible !important; }
  tr.ht-mobile { display: table-row !important; }
  td.ht-mobile { display: table-cell !important; }

  /* Column stacking */
  .ht-stack { display: block !important; width: 100% !important; max-width: 100% !important; padding-left: 0 !important; padding-right: 0 !important; }
}
/* ${HT_RESPONSIVE_BLOCK_END_MARKER} */`;
}

/** The canonical block wrapped in its own `<style>` element. */
export function buildCanonicalStyleBlock(
  config?: EmailStyleConfiguration,
): string {
  return `<style>\n${buildCanonicalInnerBlock(config)}\n</style>`;
}

// Marker so the content-width rule can be located inside the canonical `<style>`
// element (e.g. for the editor's removable block) and recognized in tests.
const HT_CONTENT_WIDTH_MARKER = "ht:content-width";

/**
 * CSS (no `<style>` wrapper) that caps and centers the rendered body at the
 * brand's content width, so a headless component rendered as its OWN document
 * (preview iframe, PNG/PDF render, agent preview, thumbnail, editor canvas) sits
 * in the same centered column a full email template provides — instead of a
 * `text-align:center` component centering across the entire viewport and reading
 * as left-aligned/stretched.
 *
 * Deliberately NOT part of `buildCanonicalInnerBlock`: that block is the shared
 * responsive `@media` vocabulary injected into REAL email-template `<head>`s and
 * shown to the agent. A `body` rule must never leak into actual sent-email HTML.
 * It only ever ships via `wrapComponentForStandaloneRender` (standalone-render paths)
 * or the editor's removable canonical style element — never via
 * `upsertCanonicalStyleBlock`.
 *
 * CSS, not a structural `<table>`: every standalone surface renders through a
 * real browser (preview iframe, Playwright PNG) that honors `body { max-width }`.
 * The downloadable HTML export is the only path opened in arbitrary email
 * clients, and it also goes through this wrapper; if a future client (e.g.
 * Outlook) needs the table idiom, reintroduce it for that path ONLY.
 */
export function buildContentWidthCss(config?: EmailStyleConfiguration): string {
  const width = resolveContentWidthPx(config?.contentWidthPx);
  return `/* ${HT_CONTENT_WIDTH_MARKER} */
body { margin: 0 auto !important; max-width: ${width}px !important; }`;
}

/**
 * The full CSS (no `<style>` wrapper) needed to render a headless component as
 * its own standalone document: the canonical responsive `@media` vocabulary plus
 * the content-width `body` rule. This is the single source for that pairing —
 * `wrapComponentForStandaloneRender` wraps it in a `<style>`, and the WYSIWYG
 * editor injects it into its own removable canvas `<style>` element (so it can
 * strip it back out before saving the headless fragment).
 */
export function buildComponentCanvasCss(
  config?: EmailStyleConfiguration,
): string {
  return `${buildCanonicalInnerBlock(config)}\n${buildContentWidthCss(config)}`;
}

// Leading CSS comment so anyone inspecting a downloaded/exported component knows
// the <style> block is render scaffolding (responsive defs + content-width
// centering) added for standalone viewing — NOT part of the stored component. It
// lives inside the block, so `stripCanonicalStyleBlock` removes it with the rest.
export const STANDALONE_RENDER_NOTE =
  "/* Style block added by Hightouch for standalone rendering only; not part of the component. */";

/** Escape a literal string for safe interpolation into a RegExp. */
function escapeRegExp(value: string): string {
  return value.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
}

/**
 * Wrap a headless component fragment for rendering it AS ITS OWN standalone
 * document — preview iframe, HTML/PDF/PNG export, thumbnail, or the agent's
 * working seed. Prepends the canonical responsive `<style>` block plus the
 * content-width `body` rule (CSS centering); the fragment is left untouched
 * after it.
 *
 * STANDALONE ONLY. Do NOT use this when the component is embedded into a host
 * email/template, or anywhere the output becomes real sent-email HTML — the
 * `body` rule and duplicated `<style>` are wrong in that context. For injecting
 * the responsive vocabulary into a real template `<head>`, use
 * `upsertCanonicalStyleBlock`; for the editor's removable canvas style, use
 * `buildComponentCanvasCss` (the same CSS without the `<style>` wrapper).
 */
export function wrapComponentForStandaloneRender(
  fragment: string,
  config?: EmailStyleConfiguration,
): string {
  const styleBlock = `<style>\n${STANDALONE_RENDER_NOTE}\n${buildComponentCanvasCss(config)}\n</style>`;
  return `${styleBlock}\n${fragment}`;
}

// Matches an existing canonical block (the comment-bounded region, markers
// inclusive). Tolerates whitespace variations inside the comment markers.
const CANONICAL_REGION_RE = new RegExp(
  `/\\*\\s*${HT_RESPONSIVE_BLOCK_START_MARKER}\\s*\\*/[\\s\\S]*?/\\*\\s*${HT_RESPONSIVE_BLOCK_END_MARKER}\\s*\\*/`,
);

export function hasCanonicalStyleBlock(html: string): boolean {
  return CANONICAL_REGION_RE.test(html);
}

const HTML_COMMENT_RE = /<!--[\s\S]*?-->/g;

/**
 * Replace HTML comment bodies with equal-length blanks so index-based searches
 * for </head> / </body> never match a literal one inside a comment (which would
 * insert the canonical block inside the comment and corrupt the document).
 * Length-preserving, so the returned index stays valid in the original string.
 */
function blankComments(html: string): string {
  return html.replace(HTML_COMMENT_RE, (m) => " ".repeat(m.length));
}

/**
 * Idempotently ensure a template's HTML contains the canonical block.
 *
 * - If the block already exists (located by markers), its contents are
 *   replaced with a freshly built block (so breakpoint changes take effect).
 *   The enclosing `<style>` is preserved.
 * - Otherwise a new `<style>` block is inserted before `</head>` (falling back
 *   to before `</body>`, then prepending) so it sits in the document head.
 *
 * String-based on purpose — never reparse email HTML.
 */
export function upsertCanonicalStyleBlock(
  html: string,
  config?: EmailStyleConfiguration,
): string {
  const inner = buildCanonicalInnerBlock(config);

  if (CANONICAL_REGION_RE.test(html)) {
    return html.replace(CANONICAL_REGION_RE, inner);
  }

  const styleBlock = buildCanonicalStyleBlock(config);

  // Search on a comment-blanked copy so a literal </head>/</body> inside a
  // comment isn't chosen as the insertion point; blanking is length-preserving
  // so the indices remain valid in the original string.
  const searchable = blankComments(html);

  const headClose = searchable.search(/<\/head\s*>/i);
  if (headClose !== -1) {
    return `${html.slice(0, headClose)}${styleBlock}\n${html.slice(headClose)}`;
  }

  const bodyClose = searchable.search(/<\/body\s*>/i);
  if (bodyClose !== -1) {
    return `${html.slice(0, bodyClose)}${styleBlock}\n${html.slice(bodyClose)}`;
  }

  return `${styleBlock}\n${html}`;
}
/**
 * Remove the deterministically-injected scaffolding added by
 * `wrapComponentForStandaloneRender` — the canonical `<style>` block (responsive
 * vocabulary + the content-width body rule) — recovering the bare headless
 * fragment. The inverse of the wrap; used on the editor save path so the stored
 * component stays headless, and on export.
 */
export function stripCanonicalStyleBlock(html: string): string {
  // Remove a <style> element that holds ONLY the canonical block (plus the
  // standalone-render note and the trailing content-width rule that
  // `wrapComponentForStandaloneRender` adds). The leading group matches only
  // whitespace or that exact note — NOT arbitrary user CSS — so a <style> that
  // also carries unrelated rules falls through to the marker-region-only strip
  // below and keeps the user's CSS.
  const noteOrSpace = `(?:\\s|${escapeRegExp(STANDALONE_RENDER_NOTE)})*`;
  const styleWrapped = new RegExp(
    `<style[^>]*>${noteOrSpace}/\\*\\s*${HT_RESPONSIVE_BLOCK_START_MARKER}\\s*\\*/[\\s\\S]*?/\\*\\s*${HT_RESPONSIVE_BLOCK_END_MARKER}\\s*\\*/[\\s\\S]*?</style\\s*>`,
    "i",
  );
  if (styleWrapped.test(html)) {
    return html.replace(styleWrapped, "");
  }
  // Otherwise just remove the marker region, leaving the surrounding <style>.
  return html.replace(CANONICAL_REGION_RE, "");
}
